import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ResultBadge, SimBadge, inr } from "./ResultBadge";

const today = () => new Date().toISOString().slice(0, 10);

export function POControlsPanel({ campaign, po, planCost, canEdit, onChange }) {
  const [pos, setPos] = useState([]);
  const [sel, setSel] = useState(campaign.aggregate_po_id || "");
  useEffect(() => { if (canEdit) api.get("/aggregate-pos").then((r) => setPos(r.data)).catch(() => {}); }, [canEdit]);
  const tenantPos = useMemo(() => pos.filter((p) => p.tenant_id === campaign.tenant_id), [pos, campaign.tenant_id]);

  const link = async () => {
    try { await api.patch(`/campaigns/${campaign.id}/plan`, { aggregate_po_id: sel || "", change_note: "PO link updated" }); toast.success("PO link updated"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const t = today();
  const validity = !po ? null : po.valid_to && po.valid_to < t ? { v: "FAIL", l: `Expired ${po.valid_to}` } : po.valid_from && po.valid_from > t ? { v: "NEEDS_REVIEW", l: `Not valid until ${po.valid_from}` } : { v: "PASS", l: `Valid ${po.valid_from} → ${po.valid_to}` };
  const timing = !po ? null : campaign.start_date && po.po_date > campaign.start_date ? { v: "FAIL", l: `PO ${po.po_date} raised after execution start ${campaign.start_date}` } : { v: "PASS", l: `PO ${po.po_date} precedes execution ${campaign.start_date || "—"}` };
  const alloc = po ? (po.allocated_to_campaign || po.total_amount) : 0;
  const budget = !po ? null : planCost > alloc ? { v: "FAIL", l: `Plan ${inr(planCost)} exceeds allocation ${inr(alloc)}` } : { v: "PASS", l: `Plan ${inr(planCost)} within allocation ${inr(alloc)}` };
  const pct = po ? Math.min(100, Math.round((po.consumed_amount / (po.total_amount || 1)) * 100)) : 0;

  return (
    <div className="grid gap-5 lg:grid-cols-3" data-testid="po-controls-panel">
      <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-5 space-y-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2"><span className="font-heading text-lg font-bold text-slate-900">{po ? po.po_number : "No PO linked"}</span><SimBadge label="HUL ERP/SAP · SIMULATED" /></div>
            <div className="text-xs text-slate-500 mt-1">{po ? `Supplier PROPAY GLOBAL PRIVATE LIMITED · PO date ${po.po_date} · status ${po.status}` : "Link the Coupa / SAP purchase order that funds this campaign."}</div>
          </div>
          {po && <ResultBadge value={validity.v} data-testid="po-validity-badge" />}
        </div>
        {po && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[["PO value", inr(po.total_amount)], ["Consumed", inr(po.consumed_amount)], ["Balance", inr(po.balance)], ["This campaign", inr(alloc)]].map(([l, v]) => (
                <div key={l}><div className="text-[10px] uppercase tracking-wider text-slate-500">{l}</div><div className="font-mono text-lg font-bold text-slate-900">{v}</div></div>
              ))}
            </div>
            <div>
              <div className="flex justify-between text-xs text-slate-500 mb-1"><span>Consumption</span><span className="font-mono">{pct}%</span></div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${pct > 90 ? "bg-rose-500" : "bg-[#0052FF]"}`} style={{ width: `${pct}%`, transition: "width .5s" }} /></div>
            </div>
            <div className="divide-y divide-slate-100 text-sm">
              {[["Validity window", validity], ["Timing (PO before execution)", timing], ["Budget sufficiency", budget], ["Plans funded by this PO", { v: po.sibling_campaigns ? "NEEDS_REVIEW" : "PASS", l: po.sibling_campaigns ? `${po.sibling_campaigns + 1} campaigns share this PO` : "Single plan" }]].map(([l, c]) => (
                <div key={l} className="flex items-center justify-between py-2.5" data-testid={`po-check-${l.split(" ")[0].toLowerCase()}`}><span className="text-slate-600">{l}</span><span className="flex items-center gap-3"><span className="text-slate-800 text-xs">{c.l}</span><ResultBadge value={c.v} /></span></div>
              ))}
            </div>
          </>
        )}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
        <div className="font-heading font-bold text-slate-900">PO relationship</div>
        {canEdit ? (
          <>
            <Select value={sel || "none"} onValueChange={(v) => setSel(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="po-link-select"><SelectValue placeholder="Select PO" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Unlink —</SelectItem>
                {tenantPos.map((p) => <SelectItem key={p.id} value={p.id}>{p.po_number} · {inr(p.total_amount)} · {p.status}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={link} className="w-full bg-[#0052FF] hover:bg-[#0043d1]" data-testid="po-link-btn">Update PO link</Button>
          </>
        ) : <p className="text-xs text-slate-500">PO links are maintained by HUL Media Finance / ProPay Operations.</p>}
        <p className="text-[11px] text-slate-400 leading-relaxed">Production dependency: HUL ERP/SAP interface for PO master, approval date and balance. Contract fields are placeholders until HUL supplies the interface spec.</p>
      </div>
    </div>
  );
}
